/**
 * Layout principal: Sidebar + Header + ContentArea.
 *
 * El padding izquierdo es dinamico segun el estado colapsado del sidebar
 * (via SidebarContext). Se añade una barra de acento superior con el color
 * del tema del tenant para dar caracter a cada tenant en todas las paginas.
 */
import { type ReactNode, useMemo } from 'react';
import { Sidebar } from '@/components/shared/layout/Sidebar/Sidebar';
import { Header } from '@/components/shared/layout/Header/Header';
import type { Crumb } from '@/components/shared/navigation/Breadcrumb';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/contexts/AuthContext';
import { resolveSidebarTheme } from '@/config/sidebar-themes';
import { cn } from '@/utils/cn';

type MainLayoutProps = {
  children: ReactNode;
  crumbs?: Crumb[];
  rightSlot?: ReactNode;
};

export function MainLayout({ children, rightSlot }: MainLayoutProps) {
  const { collapsed } = useSidebar();
  const { user } = useAuth();
  const theme = useMemo(() => {
    if (!user?.active_tenant_id) return resolveSidebarTheme(null);
    return resolveSidebarTheme(user?.tenant_sidebar_theme ?? null);
  }, [user?.active_tenant_id, user?.tenant_sidebar_theme]);

  return (
    <div className="flex min-h-screen bg-[#f5f6fa]">
      <Sidebar />
      <div className={cn('flex flex-1 flex-col transition-all duration-300 ease-in-out', collapsed ? 'lg:pl-16' : 'lg:pl-[220px]')}>
        <Header rightSlot={rightSlot} />
        <div className={cn('h-[3px] w-full shrink-0', theme.pageAccent)} aria-hidden="true" />
        <main role="main" className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}
